<template>
  <form class="cart__shipping cart__footer" @submit="shippingEstimate">
    <div class="cart__shipping-code cart__shipping-code--opened">
      <label class="cart__shipping-label">
        {{ $t('cart.shipping_estimate.calculate_shipping') }}</label
      >
      <div class="cart__shipping-fields">
        <input
          ref="inputZip"
          v-model="zipcode"
          type="text"
          name="zipcode"
          placeholder="CEP"
          class="cart__shipping-zipcode"
        />

        <v-button
          :label="$t('cart.shipping_estimate.calculate')"
          type="submit"
          name="commit"
          class="cart__shipping-btn"
          height="44px"
          :loading="shippingEstimating"
        />
      </div>
      <div class="cart__shipping__content">
        <transition name="fade">
          <ul
            v-show="!shippingEstimating && isPresent(shippingOptions)"
            class="cart__shipping__rates"
          >
            <li
              v-for="(shippingOption, idx) in shippingOptions"
              :key="idx"
              class="cart__shipping__rate"
            >
              <span class="cart__shipping__rate-description">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="shippingOption.name"></div>
              </span>
              <span
                v-if="shippingOption.old_rate"
                class="cart__shipping__rate-old-price"
                >{{ formatPrice(shippingOption.old_rate) }}</span
              >

              <b
                :class="[
                  'cart__shipping__rate-title',
                  {
                    'text--pink': shippingOption.rate === 0,
                    'text--inline': shippingOption.old_rate,
                  },
                ]"
              >
                {{
                  shippingOption.rate === 0
                    ? $t('cart.choosable_gifts.free')
                    : formatPrice(shippingOption.rate)
                }}
              </b>
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import appendUrlParam from '~/utils/appendUrlParam'
import isPresent from '~/utils/isPresent'
import cepMask from '~/commun/utils/cepMask'
import priceable from '~/mixins/priceable'
import VButton from '~/components/v-button/VButton.vue'

export default {
  name: 'ShippingEstimate',
  components: {
    VButton,
  },
  mixins: [priceable],
  props: {
    initialZipcode: String,
    cartNumber: {
      type: [Number, String],
      default: '',
    },
  },
  data() {
    return {
      zipcode: this.initialZipcode || '',
      shippingOptions: [],
      shippingEstimating: false,
    }
  },
  computed: {
    ...mapGetters('cart', [
      'isQuantityUpdated',
      'isCouponAdded',
      'isCouponRemoved',
    ]),
  },
  watch: {
    zipcode(val) {
      this.zipcode = this.cepMask(val)
      if (this.isValidZipcode()) {
        this.$emit('zipcode', this.zipcode)
      }
    },
    isQuantityUpdated(newVal) {
      if (newVal && !this.shippingEstimating && this.isValidZipcode()) {
        this.shippingEstimate()
      }
    },
    isCouponAdded(newVal) {
      if (newVal && !this.shippingEstimating && this.isValidZipcode()) {
        this.shippingEstimate()
      }
    },
    isCouponRemoved(newVal) {
      if (newVal && !this.shippingEstimating && this.isValidZipcode()) {
        this.shippingEstimate()
      }
    },
  },
  methods: {
    cepMask,
    isPresent,
    isValidZipcode() {
      return this.zipcode.length === 8
    },
    async shippingEstimate(e) {
      if (this.shippingEstimating) return
      if (e) e.preventDefault()
      this.$store.dispatch?.('analytics/emitGtagEvent', {
        name: 'calculate_shipping',
      })

      if (!this.isValidZipcode()) {
        this.$refs.inputZip.focus()
        this.$displayTopMessage(this.$t('cart.shipping_estimate.empty'), {
          type: 'error',
        })
        return
      }

      this.shippingEstimating = true

      try {
        const params = {
          zipcode: this.zipcode,
          order: this.cartNumber,
          ...this.$store.getters.storeInfo,
        }

        this.shippingOptions = await this.$axios.$get(
          appendUrlParam('/shipping_estimates/calculate', params)
        )

        if (!this.shippingOptions || this.shippingOptions.length < 1) {
          this.$displayTopMessage(
            this.$t('cart.shipping_estimate.not_exists'),
            {
              type: 'error',
            }
          )
        }
      } catch (err) {
        this.shippingOptions = []

        this.$displayTopMessage(this.$t('cart.shipping_estimate.fetch_error'), {
          type: 'error',
        })
      } finally {
        this.shippingEstimating = false
        this.$store.dispatch('cart/resetShippingFlags')
      }
    },
  },
}
</script>

<style lang="scss">
.cart__shipping {
  &.cart__footer {
    padding: 20px 0;
    .cart__shipping-label {
      display: block;
      text-transform: none;
      font-size: 15px;
      width: 100%;
      padding: 0;
      box-sizing: border-box;
      white-space: inherit;
      line-height: 18px;
      margin-top: 0;
    }
  }

  .cart__shipping__rate-message {
    background: mediumaquamarine;
    padding: 10px 15px;
    font-size: 13px;
    color: white;
    border-top: 10px solid white; /* This is embarrassing. Margin is not working, so... */
    display: block;
  }
}

.cart__shipping-code {
  padding: 0;
  margin: 20px 0 15px;
  width: 100%;
  box-sizing: border-box;

  &.cart__shipping-code--opened {
    .cart__shipping-fields {
      display: flex;
    }
  }

  .cart__shipping-label {
    display: block;
    margin: 0;
    font-size: 15px;
    line-height: 18px;
    color: #555;
    text-decoration: none;
    text-transform: none;
    cursor: pointer;
  }

  .cart__shipping-fields {
    width: 100%;
    margin: 10px 0 5px;
    display: none;
  }
  .cart__shipping-zipcode {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
    box-shadow: 0 0 0 30px white inset !important;
    line-height: normal;
    border-radius: 0;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    width: 100%;
    border-color: #e2e2e6;
  }
  .cart__shipping-btn {
    width: auto;
    min-width: 70px;
    border-radius: 0;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    border: 1px solid #e2e2e6;
    border-left: 0;
    color: #555;
    line-height: 20px;
    background-color: $color_gray;

    &:hover {
      background-color: #d2d2d6;
    }
    &:focus {
      outline: none;
      background-color: #d2d2d6;
    }
  }

  .cart__shipping__content {
    display: flex;
    flex-wrap: wrap;
  }
}

.cart__shipping-fields {
  width: 220px;
  display: flex;
}
.cart__shipping .cart__shipping-btn {
  width: 150px;
  margin-right: 0;
  line-height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cart__shipping .cart__shipping-label {
  line-height: 1.5;
  margin-top: 0;
  margin-bottom: 10px;
}
.cart__shipping__rates {
  list-style-type: none;
  margin: 0;
  padding: 0;
}
.cart__shipping__rate {
  margin-top: 0;
  line-height: 18px;
  background-color: #f6f5fa;
  padding: 15px;
  border-bottom: 1px solid #e6e6ea;
  &:last-child {
    border-bottom: 0;
  }
}
.cart__shipping__rate-title {
  display: block;
}
.cart__shipping__rate-description {
  font-size: 13px;
  display: block;

  strong {
    display: block;
  }
}
.cart__shipping__rate-old-price {
  display: inline-block;
  text-decoration: line-through;
}
.cart__shipping__content-overlay {
  margin-top: 15px;
  width: 100%;

  .loading {
    margin-left: auto;
    margin-right: auto;
    width: 30px;
    height: 30px;
  }
}

.cart__shipping__content.active {
  .cart__shipping__content-overlay {
    display: block;
  }

  .cart__shipping__rates {
    display: none;
  }
}

.text--inline {
  display: inline-block;
}

@media (min-width: $screen_tablet) {
  .cart__shipping .cart__shipping-label {
    line-height: $input_height;
    margin: 0 15px 0 0;
  }
}

@media (max-width: $screen_large) {
  .cart__shipping {
    margin-bottom: 0;
    border-bottom: 0;
    padding-bottom: 0;

    &.cart__footer {
      margin: 0;
      .cart__shipping-label {
        margin-bottom: 0;
      }
    }
  }

  .cart__shipping-code {
    padding: 0;
    margin: 0;
  }
  .cart__shipping {
    margin-top: 0;
    display: flex;

    label {
      white-space: nowrap;
      float: left;
      margin-right: 15px;
    }

    .cart__shipping-fields {
      float: left;
    }
  }
  .cart__shipping__content {
    clear: both;
  }
}
</style>
